import { IconFormNames, ID, LifeArea, NewLifeArea } from '@griply/types';

import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { withRoles } from '../utils/with-roles';

export type CreateLifeAreaOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  lifeArea: NewLifeArea;
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: string };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
};

export const createLifeArea = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>({
  lifeArea,
  userId,
  collection,
  doc,
  setDoc,
  db,
  serverTimestamp,
}: CreateLifeAreaOptions<
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb
>): Promise<LifeArea> => {
  const collectionRef = collection(db, FirestoreCollections.LifeAreas);
  const newDoc = doc(collectionRef);

  const newDocData = withRoles(
    {
      ...lifeArea,
      id: newDoc.id,
      iconName: lifeArea.iconName ?? IconFormNames.PlaceholderIcon,
      frozenAt: null,
    },
    userId,
  );

  await setDoc(newDoc, {
    ...newDocData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const now = new Date();

  return {
    ...newDocData,
    id: newDoc.id,
    iconName: lifeArea.iconName ?? IconFormNames.PlaceholderIcon,
    createdAt: now,
    updatedAt: now,
    frozenAt: null,
    archivedAt: null,
    templateId: null,
    lifescanValue: null,
    sections: null,
    goalSorting: null,
    taskSorting: null,
    visionPreview: null,
  };
};
